import * as _ from 'lodash';
import * as React from 'react';
import { default as ReactJson } from 'react-json-view';

import { loadConfig } from '../tools/config';
import { checkStatus } from '../tools/fetch_helpers';
import { onSelectInJson } from '../tools/urn_navigation';

import { VisualizationTransform } from '../tools/visualize_blob';

import { MeshViewer, looksLikeObj } from './MeshViewer';

const config = loadConfig();

function assertNever(x: never): never {
  throw new Error(`Unexpected object: ${x}`);
}

interface Props {
  type: string;
  urnid: string;
  simple: boolean;
  visualization_transform?: VisualizationTransform;
}

interface State {
  blob:
    | {
        type: 'json';
        data: any;
      }
    | {
        type: 'image';
        data: string | ArrayBuffer | null;
      }
    | {
        type: 'text';
        data: string;
      }
    | {
        type: 'mesh';
        data: string;
        urnid: string;
      }
    | {
        type: 'loading';
      }
    | {
        type: 'error';
      };
  rotationIndex: number;
}

export class BlobViewer extends React.Component<Props, State> {
  static defaultProps: Partial<Props> = {
    simple: false,
  };

  constructor(props: Props) {
    super(props);
    this.state = {
      blob: { type: 'loading' },
      rotationIndex: 1,
    };
  }

  componentWillMount() {
    this.doUrlRequest();
  }

  componentDidUpdate(prevProps: Props) {
    // Typical usage (don't forget to compare props):
    if (this.props.urnid !== prevProps.urnid) {
      // Setting the state to loading is avoided because it causes flickering of preview.
      // this.setState({ blob: { type: 'loading' } });
      this.throttledUrlRequest();
    }
  }

  doUrlRequest = async () => {
    const urnid = this.props.urnid;
    const accessToken = localStorage.getItem('access_token');
    const url = `${config.virga}/${this.props.type}/${urnid}`;
    const response = await fetch(url, {
      method: 'get',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    try {
      checkStatus(response);

      if (
        response.headers.get('content-type') === 'text/plain; charset=utf-8'
      ) {
        const data = await response.text();
        // Try parsing as JSON data.
        try {
          const json_data = JSON.parse(data);
          this.setState({ blob: { type: 'json', data: json_data } });
          return;
        } catch (err) {
          if (err.name !== 'SyntaxError') {
            throw err;
          }
        }

        if (looksLikeObj(data)) {
          this.setState({ blob: { type: 'mesh', data, urnid } });
          return;
        }

        // Show as text.
        this.setState({ blob: { type: 'text', data } });
        return;
      }

      const blob = await response.blob();
      // Image headers contain control bytes (PNG and JPEG both include NUL), text never does,
      // so only decode the whole blob if it starts as text.
      const head = await blob.slice(0, 64).text();
      if (!/[\x00-\x08\x0e-\x1f]/.test(head)) {
        const data = await blob.text();
        if (looksLikeObj(data)) {
          this.setState({ blob: { type: 'mesh', data, urnid } });
          return;
        }
      }

      // Treat as an image.
      let blob_out = blob;

      if (this.props.visualization_transform) {
        const buffer = await blob.arrayBuffer();
        const transformed = this.props.visualization_transform(buffer);
        blob_out = new Blob([transformed]);
      }

      const reader = new FileReader();
      reader.onloadend = () =>
        this.setState({
          blob: { type: 'image', data: reader.result },
        });
      reader.readAsDataURL(blob_out);
    } catch (e) {
      console.error(e);
      this.setState({ blob: { type: 'error' } });
      return;
    }
  };

  throttledUrlRequest = _.throttle(this.doUrlRequest, 100);

  rotateImageClockwise = () =>
    this.setState((state) => ({
      rotationIndex: state.rotationIndex === 4 ? 1 : state.rotationIndex + 1,
    }));

  rotateImageCounterClockwise = () =>
    this.setState((state) => ({
      rotationIndex: state.rotationIndex === 1 ? 4 : state.rotationIndex - 1,
    }));

  render() {
    switch (this.state.blob.type) {
      case 'loading':
        return <div>Loading...</div>;
      case 'error':
        return <div>Failed to load blob, see console.</div>;
      case 'text':
        return <p className="white-space-wrapping">{this.state.blob.data}</p>;
      case 'mesh':
        return (
          <MeshViewer
            key={this.state.blob.urnid}
            obj={this.state.blob.data}
            filename={`${this.state.blob.urnid}.obj`}
          />
        );
      case 'json':
        return (
          <ReactJson
            src={this.state.blob.data}
            name={null}
            indentWidth={2}
            collapsed={2}
            displayObjectSize={false}
            displayDataTypes={false}
            onSelect={onSelectInJson}
          />
        );
      case 'image':
        return (
          <div>
            {this.props.visualization_transform ? (
              <h3>(NB! Visualization is altered to aid viewing blob)</h3>
            ) : (
              <h3>(Raw visualization)</h3>
            )}
            <img
              className={`rotation-position-${this.state.rotationIndex}`}
              src={this.state.blob.data as string}
              style={{
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            />
            {!this.props.simple ? (
              <div>
                <button
                  className="button counterClockwise"
                  onClick={this.rotateImageCounterClockwise}
                >
                  Rotate Counterclockwise ⭯
                </button>
                <button
                  className="button clockwise"
                  onClick={this.rotateImageClockwise}
                >
                  Rotate Clockwise ⭮
                </button>
              </div>
            ) : null}
          </div>
        );
      default:
        return assertNever(this.state.blob);
    }
  }
}
